import { z } from "zod";
/**
 * Twin Doctor on the public surface (docs/specs/twin-doctor.md): an organisation's health as the caller may see it (its
 * vital signs and findings), its scans and settings for organisation admins, dismissing a finding, and what depends on
 * one object, read live from the estate graph's edges. Every operation is a verified person's: a bare tenant API key
 * is refused. Part of TWIN_PUBLIC_OPERATIONS. The reads of findings and impact are MCP tools too; scans, dismissals and
 * settings are not, since agents never decide what the Doctor does. The Twin itself reads the same impact through its
 * worker command `doctor.impact` (channels.ts).
 */
export const TWIN_DOCTOR_OPERATIONS = {
    getDoctorSummary: { operationId: "twin.doctor.summary", method: "GET", path: "/api/twin/doctor",
        rateClass: "read", requiredScope: "twin.read" },
    createDoctorScan: { operationId: "twin.doctor.scans.create", method: "POST", path: "/api/twin/doctor/scans",
        rateClass: "create", requiredScope: "twin.write" },
    getDoctorScan: { operationId: "twin.doctor.scans.get", method: "GET", path: "/api/twin/doctor/scans/{scanId}",
        rateClass: "read", requiredScope: "twin.read" },
    listDoctorFindings: { operationId: "twin.doctor.findings.list", method: "GET", path: "/api/twin/doctor/findings",
        rateClass: "read", requiredScope: "twin.read", mcpTool: "twin_doctor_findings" },
    getDoctorFinding: { operationId: "twin.doctor.findings.get", method: "GET",
        path: "/api/twin/doctor/findings/{findingId}", rateClass: "read", requiredScope: "twin.read", mcpTool: "twin_doctor_show" },
    dismissDoctorFinding: { operationId: "twin.doctor.findings.dismiss", method: "POST",
        path: "/api/twin/doctor/findings/{findingId}/dismiss", rateClass: "create", requiredScope: "twin.write" },
    liftDoctorFinding: { operationId: "twin.doctor.findings.lift", method: "POST",
        path: "/api/twin/doctor/findings/{findingId}/lift", rateClass: "create", requiredScope: "twin.write" },
    getDoctorSettings: { operationId: "twin.doctor.settings.get", method: "GET", path: "/api/twin/doctor/settings",
        rateClass: "read", requiredScope: "twin.read" },
    updateDoctorSettings: { operationId: "twin.doctor.settings.update", method: "PUT", path: "/api/twin/doctor/settings",
        rateClass: "create", requiredScope: "twin.write" },
    getDoctorImpact: { operationId: "twin.doctor.impact", method: "GET", path: "/api/twin/doctor/impact",
        rateClass: "read", requiredScope: "twin.read", mcpTool: "twin_doctor_impact" },
};
/** Stable scan conflict reasons, shared by the server and its clients. */
export const TWIN_DOCTOR_SCAN_REASONS = {
    notRunning: "doctor_not_running", disabled: "doctor_disabled", active: "scan_active",
};
export const DOCTOR_UNREADABLE_OBJECT = "a data set you can't open";
export const DOCTOR_UNREADABLE_CHANNEL = "a channel you can't open";
/** The seven families a check belongs to, in the order a scan runs them. */
export const twinDoctorFamilySchema = z.enum(["broken", "drifted", "stale", "untrusted", "duplicated", "dead", "tangled"]);
export const twinDoctorSeveritySchema = z.enum(["critical", "high", "medium", "low", "info"]);
export const twinDoctorConfidenceSchema = z.enum(["certain", "likely", "possible"]);
/** Open until fixed (resolved) or dismissed (suppressed while its dismissal lasts). */
export const twinDoctorFindingStatusSchema = z.enum(["open", "suppressed", "resolved"]);
export const twinDoctorScanStatusSchema = z.enum(["queued", "running", "succeeded", "failed", "cancelled"]);
/** The family of checks a scan is running, `queued` before it starts and `finished` once it has succeeded. */
export const twinDoctorScanPhaseSchema = z.enum(["queued", ...twinDoctorFamilySchema.options, "finished"]);
const doctorId = z.string().min(1);
const count = z.number().int().nonnegative();
const epochMs = z.number().int().nonnegative();
/** How much of a scan's checks ran. A check that did not run is not checked, and never counts as healthy. */
export const twinDoctorCoverageSchema = z.object({
    ran: count,
    notChecked: count,
    /** Checks a queued or running scan has still to reach. */
    pending: count,
}).strict();
/** The viewer's vital signs: what they may see of the organisation's open findings, and its last scan. */
export const twinDoctorSummaryResponseSchema = z.object({
    /** Whether the organisation's Doctor is switched on. */
    enabled: z.boolean(),
    /** Whether this server runs the Doctor's scan worker; without it no scan starts here. */
    workerRunning: z.boolean(),
    /** Open findings the viewer may see; findings they may not are neither shown nor counted. */
    open: z.object({
        total: count,
        bySeverity: z.object({ critical: count, high: count, medium: count, low: count, info: count }).strict(),
        byFamily: z.object({ broken: count, drifted: count, stale: count, untrusted: count, duplicated: count,
            dead: count, tangled: count }).strict(),
    }).strict(),
    /** The last scan that finished, however it ended. */
    lastScan: z.object({
        scanId: doctorId,
        status: z.enum(["succeeded", "failed", "cancelled"]),
        finishedAt: epochMs,
        coverage: twinDoctorCoverageSchema,
    }).strict().nullable(),
    /** The scan queued or running now, if there is one. */
    activeScan: z.object({
        scanId: doctorId,
        status: z.enum(["queued", "running"]),
        phase: twinDoctorScanPhaseSchema,
    }).strict().nullable(),
}).strict();
/** A manual scan of the whole organisation; it takes nothing yet. */
export const twinDoctorScanCreateInputSchema = z.object({}).strict();
export const twinDoctorScanCreatedResponseSchema = z.object({ scanId: doctorId, status: z.literal("queued") }).strict();
/** What one check did in a scan: ran (with how many findings it raised), not checked (and why), or still pending. */
export const twinDoctorScanCheckSchema = z.object({
    checkId: doctorId,
    title: z.string().min(1),
    family: twinDoctorFamilySchema.nullable(),
    status: z.enum(["ran", "not_checked", "pending"]),
    version: z.number().int().positive().optional(),
    findings: count.optional(),
    reason: z.string().optional(),
}).strict();
export const twinDoctorScanSchema = z.object({
    id: doctorId,
    trigger: z.enum(["schedule", "manual"]),
    /** Who asked for a manual scan; a scheduled one names nobody. */
    requestedBy: doctorId.nullable(),
    status: twinDoctorScanStatusSchema,
    phase: twinDoctorScanPhaseSchema,
    requestedAt: epochMs,
    startedAt: epochMs.nullable(),
    finishedAt: epochMs.nullable(),
    attempts: count,
    probeCount: count,
    error: z.string().nullable(),
    coverage: twinDoctorCoverageSchema,
    checks: z.array(twinDoctorScanCheckSchema),
}).strict();
/**
 * One finding as the viewer may see it. Its subject name comes from the current readable row. Wherever evidence names
 * something in a channel the viewer can't open, that thing is "a data set you can't open" in "a channel you can't open", without
 * its ids.
 */
export const twinDoctorFindingSchema = z.object({
    id: doctorId,
    checkId: z.string().regex(/^[A-Z]{3}-[0-9]{2}$/u),
    checkVersion: z.number().int().positive(),
    title: z.string().min(1),
    family: twinDoctorFamilySchema,
    severity: twinDoctorSeveritySchema,
    confidence: twinDoctorConfidenceSchema,
    status: twinDoctorFindingStatusSchema,
    /** What it is about, as the estate graph names objects. */
    subject: z.object({ kind: z.string().min(1), id: z.string().min(1) }).strict(),
    /** Current subject row's name, or null when missing, unsupported or unreadable to this viewer. */
    subjectName: z.string().nullable(),
    /** The subject's own channel; none for an organisation-wide finding. */
    channel: z.object({ id: doctorId, name: z.string().nullable() }).strict().nullable(),
    /** For organisation admins only: findings on view contracts, the catalog and the warehouse. */
    adminOnly: z.boolean(),
    evidence: z.record(z.string(), z.unknown()),
    firstSeenAt: epochMs,
    lastSeenAt: epochMs,
    resolvedAt: epochMs.nullable(),
    /** The dismissal that holds it suppressed, while it lasts. */
    suppression: z.object({
        reason: z.string().min(1),
        /** Until when; null until someone lifts it. */
        until: epochMs.nullable(),
        dismissedBy: doctorId,
        dismissedAt: epochMs,
    }).strict().nullable(),
}).strict();
/** A page of the findings the viewer may see, newest last seen first. */
export const twinDoctorFindingsListQuerySchema = z.object({
    family: twinDoctorFamilySchema.optional(),
    severity: twinDoctorSeveritySchema.optional(),
    status: twinDoctorFindingStatusSchema.default("open"),
    /** Only findings on things in this channel. */
    channel: z.string().trim().min(1).max(200).optional(),
    cursor: z.string().trim().min(1).max(500).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
}).strict();
export const twinDoctorFindingsListResponseSchema = z.object({
    findings: z.array(twinDoctorFindingSchema),
    nextCursor: z.string().nullable(),
}).strict();
export const twinDoctorFindingIdSchema = z.object({ findingId: z.string().trim().min(1).max(200) }).strict();
/** Dismissing a finding: why, and until when (epoch ms, in the future); without `until`, until someone lifts it. */
export const twinDoctorDismissInputSchema = z.object({
    reason: z.string().trim().min(1).max(2000),
    until: z.number().int().positive().optional(),
}).strict();
/** Lifting removes the dismissal without changing whether the finding has been resolved. */
export const twinDoctorLiftInputSchema = z.object({}).strict();
/** A threshold a check reads, by name, such as how many days unread counts as dead. */
const thresholdName = z.string().regex(/^[A-Za-z][A-Za-z0-9_.-]{0,99}$/u);
export const twinDoctorThresholdsSchema = z.record(thresholdName, z.number().finite())
    .refine((thresholds) => Object.keys(thresholds).length <= 50, { message: "At most 50 thresholds." });
/** An organisation's Doctor. Applying fixes stays off: it is not settable until fixes ship. */
export const twinDoctorSettingsSchema = z.object({
    enabled: z.boolean(),
    applyEnabled: z.boolean(),
    thresholds: z.record(z.string(), z.number()),
    /** Whether this server runs the Doctor's scan worker. */
    workerRunning: z.boolean(),
    updatedAt: epochMs.nullable(),
    updatedBy: doctorId.nullable(),
}).strict();
/** Sets what it names and keeps the rest; `thresholds` replaces them all. */
export const twinDoctorSettingsUpdateInputSchema = z.object({
    enabled: z.boolean().optional(),
    thresholds: twinDoctorThresholdsSchema.optional(),
}).strict().refine((input) => input.enabled !== undefined || input.thresholds !== undefined, { message: "Name what to change: enabled or thresholds." });
/** An impact names at most this many dependents; `truncated` says more were found. */
export const TWIN_DOCTOR_IMPACT_LIMIT = 500;
/**
 * What an impact is asked about. A data set is a channel_object_types id, a field that id with its key, a view and a
 * channel their ids, a view contract a legacy publication's or a Twin Logic product's id, and a relation its
 * schema.name. View contracts and relations are for organisation admins.
 */
export const twinDoctorObjectKindSchema = z.enum(["dataset", "field", "view", "channel", "view_contract", "relation"]);
/**
 * The edge of the estate graph a dependent comes through: the spec's edge table, with the edges an impact adds (a
 * data set's own views, a channel's own workflows and Slack links, one view contract depending on another).
 */
export const twinDoctorEdgeSchema = z.enum([
    "reference_field", "lookup_field", "lookup_key_value", "workflow_source", "linked_view", "widget", "composed_source",
    "own_view", "dashboard_arrangement", "pin", "home_workflow", "external_channel_binding",
    "contract_binding", "skill_binding", "contract_dependency", "relation_binding", "relation_dependency",
]);
export const twinDoctorDependentKindSchema = z.enum([
    "field", "workflow", "view", "dashboard", "widget", "dataset", "arrangement", "pin", "external_channel", "skill",
    "view_contract", "relation",
]);
const RELATION_NAME = /^(?:mlx_curated|connector_warehouse)\.[a-z_][a-z0-9_]{0,62}$/u;
export const twinDoctorImpactQuerySchema = z.object({
    kind: twinDoctorObjectKindSchema,
    id: z.string().trim().min(1).max(300),
    /** The field's key, for a field. */
    fieldKey: z.string().trim().min(1).max(200).optional(),
}).strict().superRefine((query, context) => {
    if ((query.kind === "field") !== (query.fieldKey !== undefined)) {
        context.addIssue({ code: "custom", path: ["fieldKey"], message: "A field is named by its data set's id and its key." });
    }
    if (query.kind === "relation" && !RELATION_NAME.test(query.id)) {
        context.addIssue({ code: "custom", path: ["id"],
            message: "A relation is named schema.name, in mlx_curated or connector_warehouse." });
    }
});
/** One thing that depends on the object, and how. */
export const twinDoctorDependentSchema = z.object({
    edge: twinDoctorEdgeSchema,
    /** Whether today's archive or retire guards refuse while this dependent remains. */
    guarded: z.boolean(),
    /** `may_reference` when the edge is free text, as a pin's address is. */
    certainty: z.enum(["certain", "may_reference"]),
    kind: twinDoctorDependentKindSchema,
    id: z.string().min(1),
    name: z.string(),
    channelId: z.string().min(1).nullable(),
    channelName: z.string().nullable(),
    /** The data set a dependent field, view or source belongs to. */
    objectTypeId: z.string().min(1).optional(),
    objectTypeName: z.string().optional(),
    fieldKey: z.string().min(1).optional(),
    /** How it depends, in a few words. */
    detail: z.string().optional(),
}).strict();
export const twinDoctorRetirementBlockerSchema = z.object({ code: z.string().min(1), detail: z.string() }).strict();
export const twinDoctorImpactResponseSchema = z.object({
    object: z.object({
        kind: twinDoctorObjectKindSchema,
        id: z.string().min(1),
        fieldKey: z.string().min(1).optional(),
        name: z.string(),
        /** Its lifecycle state (active, archived, paused, inactive), or for a relation what it is (view, table, missing). */
        status: z.string(),
        channelId: z.string().min(1).nullable(),
        channelName: z.string().nullable(),
        /** For a view contract: a legacy publication or a Twin Logic product. */
        contractKind: z.enum(["legacy_publication", "twin_logic"]).optional(),
    }).strict(),
    dependents: z.array(twinDoctorDependentSchema).max(TWIN_DOCTOR_IMPACT_LIMIT),
    /** Dependents the caller may not open: counted, never named. */
    hiddenCount: z.number().int().nonnegative(),
    /** More dependents were found than are named. */
    truncated: z.boolean(),
    /** For a relation: distinct agent sessions whose governed queries read it in the last 30 days. */
    agentSessions30d: z.number().int().nonnegative().optional(),
    /** For a relation that is a Twin derived relation: what refuses its retirement today. */
    retirementBlockers: z.array(twinDoctorRetirementBlockerSchema).optional(),
}).strict();
export const twinDoctorPathParamsSchema = z.object({
    scanId: twinDoctorScanSchema.shape.id.optional(), findingId: twinDoctorFindingIdSchema.shape.findingId.optional(),
}).strict();
